import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { CommunicationService } from 'src/services/communication.service';
import { ErrorReportService } from 'src/services/error-report.service';
import { HttpService } from 'src/services/http.service';
import { OneSignalService } from 'src/services/onesignal.service';

@Component({
  selector: 'app-user-process-list-question-dialog',
  templateUrl: './user-process-list-question-dialog.component.html',
  styleUrls: ['./user-process-list-question-dialog.component.scss']
})
export class UserProcessListQuestionDialogComponent implements OnInit {

  currentTest: any = null;
  questions: Array<any> = [];
  formQuestions: FormGroup;
  submiting: boolean = false;
  answeredQuestions: Array<any> = [];
  userId: string = null;
  onlyShow: boolean = false;
  processListId: string;
  processListData: any;
  user:any;
  isSmallScreen$: Observable<boolean>;
  columns$: Observable<number>;
  rowHeight$: Observable<string>;
  totalQuestions = 0;
  totalCorrectAnswers = 0;

  constructor(
    private http: HttpService,
    private errorReport: ErrorReportService,
    private snackbar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) data: any,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<UserProcessListQuestionDialogComponent>,
    private oneSignalService: OneSignalService,
    private communicationService: CommunicationService,
    private breakpoint: BreakpointObserver
  ) {

    this.currentTest = data.test;
    this.userId = data.userId;
    this.onlyShow = data.onlyShow || false;
    this.processListId = data.processListId;
    this.processListData = data.processList;

    this.formQuestions = fb.group({
      processList: [data.processListId, Validators.required],
      test: [this.currentTest.id, Validators.required],
      userId: [this.userId, Validators.required],
      questions: fb.array([])
    })
  }

  public get Questions(): FormArray {
    return this.formQuestions.get('questions') as FormArray;
  }

  ngOnInit(): void {

    this.isSmallScreen$ = this.breakpoint
      .observe([Breakpoints.XSmall, Breakpoints.HandsetPortrait])
      .pipe(
        map(state => {
          return state.matches
        })
      )

      this.columns$ = this.breakpoint.observe([
        Breakpoints.XSmall,  // Celulares pequenos
        Breakpoints.Small,   // Celulares grandes
        Breakpoints.Medium,  // Tablets
        Breakpoints.Large,   // Desktop
        Breakpoints.XLarge   // Monitores grandes
      ]).pipe(
        map(state => {
          if (state.breakpoints[Breakpoints.XSmall] || state.breakpoints[Breakpoints.Small]) {
            return 1; // Mobile: 1 coluna
          } else if (state.breakpoints[Breakpoints.Medium]) {
            return 3; // Tablet: 3 colunas
          } else {
            return 4; // Desktop: 4 colunas
          }
        })
      );

      this.rowHeight$ = this.columns$.pipe(
        map(cols => {
          switch (cols) {
            case 1: return '8:1';  // Mobile: Mais espaço vertical
            case 3: return '5:1';  // Tablet: Média altura
            case 4: return '4:1';  // Desktop: Padrão
            default: return '4:1';
          }
        })
      );

    const subject = new Subject();
    subject.subscribe(
      (result: any) => {

        const { trigger, scope } = result;
        if (
          scope.indexOf('user_process_list:response') > -1
          || scope.indexOf('user_process_list:question') > -1
        )
          this.submiting = trigger === 'pre';

      }
    )
    this.http.setLoadingIndicator(subject)

    const params = {
      id: this.currentTest.id
    }

    this.http.list('user_process_list:question', params)
      .subscribe(
        (result: any) => {
          this.totalQuestions = result.data.length;
          const controls = (this.formQuestions.get('questions') as FormArray);
          for (const question of result.data) {

            this.questions.push({
              id: question.id,
              description: question.description,
              type: question.question_type,
              options: question.options
            })
            controls.push(this.fb.group({
              id: [question.id, Validators.required],
              type: [question.question_type, Validators.required],
              response: [null, Validators.required],
              isRight: [false, Validators.required]
            }));
          }

          if (this.currentTest.status !== 'pending')
            this.getResponses();
        },
        err => this.errorReport.showMessage(err)
      );

  }

  getResponses() {
    const params = {
      test: this.currentTest.id,
      userId: this.userId,
      processList: this.processListId
    };
  
    this.http.list('user_process_list:response', params).subscribe(
      (result: any) => {
        this.answeredQuestions = result.data;
  
        // Itera sobre todas as questões
        for (let i = 0; i < this.questions.length; i++) {
          const question = this.questions[i];
          if (!question) continue;
  
          // Verifica se a questão é do tipo 'dissertative'
          if (question.type === 'dissertative') {
  
            // Localiza o response correto no array answeredQuestions
            const matchingResponse = this.answeredQuestions.find(
              (answered) => answered.selection_test_question === question.id
            );
  
            // Se encontrar um response correspondente, define o valor
            if (matchingResponse) {
              this.Questions.at(i).get('response').setValue(matchingResponse.response);
            }
          }
        }
      },
      (err) => this.errorReport.showMessage(err)
    );
  }
  

  getIsCorrect(question, option = null) {

    const response = (option === null)
      ? this.answeredQuestions.find(response => response.selection_test_question == question.id && response.approved == 1)
      : this.answeredQuestions.find(response => response.selection_test_question == question.id && response.selection_test_question_option == option.id && response.approved == 1)

    return response

  }

  getAnsweredQuestion(question, option = null) {
    // Filtra as respostas associadas à questão
    const responses = this.answeredQuestions.filter(
      response => response.selection_test_question == question.id
    );
  
    if (!responses || responses.length === 0){
      this.updateAnswerStatistics();
      return false;
    } 
  
    // Se uma opção específica for fornecida, verifica a resposta para a opção
    if (option !== null) {
      const matchedResponse = responses.find(response => {
        // Verifica se a resposta é uma string com valores separados por vírgula
        if (typeof response.response === 'string' && response.response.includes(',')) {
          const values = response.response.split(',').map(item => item.trim()); // Transforma em array
          return values.includes(option.id.toString()); // Verifica se o option.id está presente
        }
        // Caso contrário, faz a comparação direta
        return response.selection_test_question_option == option.id;
      });

      this.updateAnswerStatistics(); // Atualiza os totais
      
      // Retorna o option.id correspondente, se encontrado
      return matchedResponse ? option.id : false;
    }else if (option == null){
      this.updateAnswerStatistics(); // Atualiza os totais
      const response = this.answeredQuestions.find(response => response.selection_test_question == question.id && response.approved == 1);
      return response;
    }
    
    this.updateAnswerStatistics(); // Atualiza os totais
    // Caso contrário, retorna o valor padrão para dissertativa
    return question.type === 'dissertative' ? responses[0].response : false;
  }

  updateAnswerStatistics() {
    // Obtem o conjunto de IDs das perguntas respondidas corretamente (sem duplicatas)
    const uniqueCorrectQuestions = new Set(
      this.answeredQuestions
        .filter(q => q.approved == 1) // Filtra apenas as respostas corretas
        .map(q => q.selection_test_question) // Pega apenas os IDs das perguntas
    );
  
    this.totalCorrectAnswers = uniqueCorrectQuestions.size; // Conta apenas perguntas únicas
  }
  
  // getAnsweredQuestion(question, option = null) {

  //   const response = (option === null)
  //     ? this.answeredQuestions.find(response => response.selection_test_question == question.id && response.approved == 1)
  //     : this.answeredQuestions.find(response => response.selection_test_question == question.id && response.selection_test_question_option == option.id/* && response.approved == 1*/)

  //   if (!response)
  //     return false;

  //   return question.type === 'dissertative' ? response.response : response.selection_test_question_option;

  // }

  // setOption(control: FormControl, option,  question?:any) {
   
  //   if (this.currentTest.status !== 'pending') return;
  //   if(question.type !== 'multiple'){
  //   control.get('response').setValue(option.id);
  //   control.get('isRight').setValue(option.is_right);
  //   }else{
  //     control.get('isRight').setValue(option.is_right);
  //   }

  // }

  setOption(control: FormControl, option, question?: any) {
    
    if (this.currentTest.status !== 'pending') return;
  
    // Se a pergunta for do tipo 'option' e houver múltiplas respostas corretas
    if (question.type === 'option') {
      let selectedResponses = control.get('response').value || []; // Garante que seja um array
      
      // Se a opção já foi selecionada, remove-a; caso contrário, adiciona
      if (selectedResponses.includes(option.id)) {
        selectedResponses = selectedResponses.filter(id => id !== option.id);
      } else {
        // Limita a 2 respostas
        if (selectedResponses.length < 2) {
          selectedResponses.push(option.id);
        }
      }
  
      // Atualiza os valores
      control.get('response').setValue(selectedResponses);
      control.get('isRight').setValue(this.checkIfCorrect(selectedResponses, question.options));
    } 
    else if (question.type === 'multiple') {
      // Lógica para múltiplas respostas
      control.get('isRight').setValue(option.is_right);
    } 
    else {
      // Lógica padrão para outros tipos
      control.get('response').setValue(option.id);
      control.get('isRight').setValue(option.is_right);
    }
  }
  
  checkIfCorrect(selectedResponses: any[], options: any[]): boolean {
    // Converte is_right para número e filtra as opções corretas
    const correctOptions = options
      .filter(o => Number(o.is_right) === 1) // Garante que is_right seja tratado como número
      .map(o => String(o.id)); // Converte os IDs para string para garantir a consistência

    // Converte selectedResponses para string para comparação uniforme
    const normalizedResponses = selectedResponses.map(id => String(id));

    // Verifica se o número de respostas está correto e se todas estão entre as corretas
    return (
      normalizedResponses.length === correctOptions.length &&
      normalizedResponses.every(id => correctOptions.includes(id))
    );
  }

  onChangeMultiple(control: FormControl, args: MatCheckboxChange) {

    const { checked, source: { value } } = args;
    let response = control.get('response').value;
    if (response === null)
      response = [];

    let newResponse = response;

    if (checked === false) {
      const findIndex = response.indexOf(value)
      response = response.splice(findIndex, 1);
    } else newResponse.push(value)
    control.patchValue({ response: newResponse }, { emitEvent: false })

  }

  submitQuestion() {

    if (!confirm('Atenção, após salvar a resposta desse questionário, não poderá ser mais alterada. Deseja continuar?'))
      return;

    const isValid = !this.formQuestions.invalid;
    const data = this.formQuestions.getRawValue();

    if (isValid) {

      this.http.save('user_process_list:response', data)
        .subscribe(
          (result: any) => {

            if (result.type === 'OK') {
              this.http.save('onsignal:getCompanySing', {id: this.currentTest.company})
              .subscribe(
                (result: any) => {
                  this.user = JSON.parse(localStorage.getItem('token'));
                  if (result.type === 'OK') {
                    //console.log(result);
                    const processname = this.processListData.name;
                    const signatureIds = [result.data[0].signature_id]; // Extrai os signature_ids
                    const notification_for_all = null;
                    const title = 'Testes';
                    const fullname = this.user.firstname+' '+this.user.lastname;
                    const message = this.oneSignalService.listMessage(6, fullname, processname );
                    
                    // Envia os dados para a função do backend que lida com as notificações
                    this.oneSignalService.sendNotification(signatureIds, title, message, notification_for_all)
                    .subscribe(
                      (result: any) => {
                        if (result.type === 'OK') {
                          this.oneSignalService.listNotification();
                        } else {
                          console.error('Erro ao enviar notificação sobre o processo seletivo');
                        }
                      },
                      err => {
                        console.error('Erro ao enviar notificação ao recrutador', err);
                        this.errorReport.showMessage(err);
                      }
                    );
                  } else {
                    console.error('Erro ao enviar notificação');
                  }
                },
                err => this.errorReport.showMessage(err)
              )

              this.snackbar.open('Teste foi respondido com sucesso', 'OK', { duration: 3000 })
              this.communicationService.triggerReload(); // Dispara o evento
              this.dialogRef.close(true);
            }
          },
          err => this.errorReport.showMessage(err)

        )

    }

  }

}
